import React, { useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  IconButton,
  Tooltip,
  Stack,
  Chip,
  Typography,
  CircularProgress,
  Link as MuiLink,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import LinkOffIcon from "@mui/icons-material/LinkOffOutlined";
import SwapIcon from "@mui/icons-material/SwapHorizOutlined";
import HistoryIcon from "@mui/icons-material/HistoryOutlined";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOffOutlined";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAltOutlined";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLongOutlined";
import SettingsIcon from "@mui/icons-material/SettingsOutlined";
import { useNavigate } from "react-router-dom";

const TYPE_LABEL = { 1: "Motor", 2: "Tank", 3: "Valve", 4: "Flow" };
const TYPE_COLOR = {
  1: { bg: "#eef3fb", fg: "#0a4f9e" },
  2: { bg: "#e8f5ec", fg: "#107e3e" },
  3: { bg: "#fdf2e6", fg: "#df6e0c" },
  4: { bg: "#f3eafd", fg: "#6f3cb1" },
};

const TypeChip = ({ type }) => {
  const c = TYPE_COLOR[type] || TYPE_COLOR[1];
  return (
    <Chip
      label={TYPE_LABEL[type] || type || "—"}
      size="small"
      sx={{
        height: 20,
        fontSize: "0.6875rem",
        fontWeight: 600,
        backgroundColor: c.bg,
        color: c.fg,
      }}
    />
  );
};

// Desktop action row keeps tooltips for compactness; mobile uses a tighter
// no-tooltip variant matching the user-card layout.
const ActionRow = ({
  d,
  isStatusOpen,
  isConfigOpen,
  sendingUpdateImei,
  onToggleStatus,
  onToggleConfig,
  onEdit,
  onDelete,
  onUnlink,
  onSwap,
  onLogs,
  onUpdate,
  navigate,
  justifyContent = "flex-end",
  compact = false,
}) => {
  const Btn = ({ children, ...rest }) => (
    <IconButton size="small" sx={compact ? { p: 0.5 } : undefined} {...rest}>
      {children}
    </IconButton>
  );
  const Wrap = ({ title, children }) =>
    compact ? (
      <span>{children}</span>
    ) : (
      <Tooltip title={title}>
        <span>{children}</span>
      </Tooltip>
    );
  return (
    <Stack
      direction="row"
      spacing={compact ? 0 : 0.25}
      justifyContent={justifyContent}
      flexWrap="nowrap"
      sx={compact ? { width: "100%" } : undefined}
    >
      <Wrap title={isStatusOpen ? "Close status" : "Live status"}>
        <Btn
          onClick={() => onToggleStatus(d)}
          color={isStatusOpen ? "primary" : "default"}
        >
          {isStatusOpen ? (
            <VisibilityOffIcon fontSize="small" />
          ) : (
            <VisibilityIcon fontSize="small" />
          )}
        </Btn>
      </Wrap>
      <Wrap title={isConfigOpen ? "Close config" : "Configure (c1–c40)"}>
        <Btn
          onClick={() => onToggleConfig?.(d)}
          color={isConfigOpen ? "primary" : "default"}
        >
          <SettingsIcon fontSize="small" />
        </Btn>
      </Wrap>
      {onLogs && (
        <Wrap title="View logs">
          <Btn onClick={() => onLogs(d)}>
            <HistoryIcon fontSize="small" />
          </Btn>
        </Wrap>
      )}
      <Wrap title="View tickets">
        <Btn
          onClick={() => navigate(`/tickets?imei=${encodeURIComponent(d.IMEI)}`)}
        >
          <ReceiptLongIcon fontSize="small" />
        </Btn>
      </Wrap>
      <Wrap title="Send software update">
        <Btn
          disabled={sendingUpdateImei === d.IMEI}
          onClick={() => onUpdate?.(d)}
        >
          <SystemUpdateAltIcon fontSize="small" />
        </Btn>
      </Wrap>
      <Wrap title="Edit">
        <Btn onClick={() => onEdit(d)}>
          <EditIcon fontSize="small" />
        </Btn>
      </Wrap>
      {onSwap && (
        <Wrap title="Swap IMEI with another device">
          <Btn onClick={() => onSwap(d)}>
            <SwapIcon fontSize="small" sx={{ color: "warning.dark" }} />
          </Btn>
        </Wrap>
      )}
      {onUnlink && (
        <Wrap title="Remove from this company">
          <Btn onClick={() => onUnlink(d)}>
            <LinkOffIcon fontSize="small" sx={{ color: "warning.main" }} />
          </Btn>
        </Wrap>
      )}
      {onDelete && (
        <Wrap title="Delete">
          <Btn onClick={() => onDelete(d)}>
            <DeleteIcon fontSize="small" sx={{ color: "error.main" }} />
          </Btn>
        </Wrap>
      )}
    </Stack>
  );
};

// Persists the scroll position of a scrollable element under a string key
// so it survives unmount → remount (e.g. clicking into a device, then
// back to the list). Saves on scroll via rAF throttling; restores once
// `items` has rendered so there's actual content to scroll into.
const SCROLL_PREFIX = "devices-scroll:";

const useScrollMemory = (key, itemCount) => {
  const ref = useRef(null);
  // rAF coalescing so we don't write to sessionStorage on every scroll
  // pixel — once per frame is plenty.
  const rafRef = useRef(0);

  // Restore: after items render (DOM has scrollable content), set
  // scrollTop from sessionStorage. Re-runs if the row count changes from
  // 0 → N on first load.
  useEffect(() => {
    if (!key) return;
    const el = ref.current;
    if (!el) return;
    if (itemCount === 0) return;
    const saved = Number(
      sessionStorage.getItem(SCROLL_PREFIX + key) || 0
    );
    if (saved > 0) el.scrollTop = saved;
    // We only want to restore once per mount; subsequent renders should
    // not reset the user's scroll. The deps below mean: run on first
    // populated render and on key change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, itemCount > 0]);

  // Save: rAF-throttled listener bound to the scrollable element.
  useEffect(() => {
    if (!key) return;
    const el = ref.current;
    if (!el) return;
    const onScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0;
        sessionStorage.setItem(
          SCROLL_PREFIX + key,
          String(el.scrollTop || 0)
        );
      });
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [key]);

  return ref;
};

// Renders a populated-company cell. Devices fetched via the global
// /api/devices list come back with `company` populated to an object;
// standalone devices have `company: null`.
const CompanyCell = ({ device }) => {
  const c = device?.company;
  if (!c) {
    return (
      <Typography
        variant="caption"
        color="text.disabled"
        sx={{ fontStyle: "italic", fontSize: "0.75rem" }}
      >
        — unassigned —
      </Typography>
    );
  }
  return (
    <Stack spacing={0} sx={{ minWidth: 0 }}>
      <Typography
        variant="body2"
        sx={{ fontSize: "0.8125rem", fontWeight: 500 }}
        noWrap
      >
        {c.name || "—"}
      </Typography>
      {c.manufacturing_id ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "0.6875rem", fontFamily: "monospace" }}
        >
          #{c.manufacturing_id}
        </Typography>
      ) : null}
    </Stack>
  );
};

export default function DeviceTable({
  items,
  loading,
  activeStatusImei,
  activeConfigImei,
  onToggleStatus,
  onToggleConfig,
  onEdit,
  onDelete,
  onUnlink,
  onSwap,
  onLogs,
  onUpdate,
  onAddFirst,
  sendingUpdateImei = null,
  showCompany = false,
  // Unique key under which this table's scroll position is persisted in
  // sessionStorage. Two list pages (the global Devices view and each
  // company's Devices tab) need separate keys so they don't trample
  // each other. Omit to disable scroll memory.
  scrollKey,
}) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const scrollRef = useScrollMemory(scrollKey, items.length);

  if (isMobile) {
    return (
      // Flex-grow with internal vertical scroll. Parents (DevicesPage,
      // CompanyDevicesTab) provide the bounded flex column; this just
      // fills the rest of the height and scrolls its card list.
      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          minHeight: 0,
          p: 1,
          backgroundColor: "background.default",
          overflowY: "auto",
          overflowX: "hidden",
          width: "100%",
          maxWidth: "100%",
        }}
      >
        {loading && items.length === 0 && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress size={20} />
          </Box>
        )}
        {!loading && items.length === 0 && (
          <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
            <Typography variant="caption" display="block">
              No devices yet.
            </Typography>
            {onAddFirst && (
              <MuiLink component="button" onClick={onAddFirst} sx={{ fontSize: "0.75rem", mt: 0.5 }}>
                Add the first one
              </MuiLink>
            )}
          </Box>
        )}
        <Stack spacing={0.75}>
          {items.map((d) => {
            const isStatusOpen = activeStatusImei === d.IMEI;
            const isConfigOpen = activeConfigImei === d.IMEI;
            const labelSx = {
              fontSize: "0.75rem",
              color: "text.secondary",
              fontWeight: 600,
              minWidth: 70,
              flexShrink: 0,
            };
            const valueSx = {
              fontSize: "0.875rem",
              color: "text.primary",
              fontWeight: 500,
              wordBreak: "break-word",
            };
            return (
              <Paper
                key={d._id}
                variant="outlined"
                sx={{
                  p: 1.25,
                  // Plain light card — matches the rest of the surface.
                  // Only the border tints when a row's status panel is open,
                  // so the active card is still easy to spot.
                  backgroundColor: "background.paper",
                  borderColor: isStatusOpen ? "primary.main" : "divider",
                  transition: "border-color .15s, box-shadow .15s",
                  "&:hover": {
                    borderColor: "primary.light",
                    boxShadow: 1,
                  },
                }}
              >
                <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={1} sx={{ minWidth: 0 }}>
                    <Typography sx={labelSx}>IMEI:</Typography>
                    <Typography
                      sx={{
                        ...valueSx,
                        fontFamily: "monospace",
                        wordBreak: "break-all",
                        minWidth: 0,
                      }}
                    >
                      {d.IMEI || "—"}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    <Typography sx={labelSx}>Name:</Typography>
                    <Typography sx={valueSx}>{d.device_name || "—"}</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography sx={labelSx}>Type:</Typography>
                    <TypeChip type={Number(d.device_type)} />
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    <Typography sx={labelSx}>Model:</Typography>
                    <Typography sx={valueSx}>{d.model || "—"}</Typography>
                  </Stack>
                  {showCompany && (
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <Typography sx={labelSx}>Company:</Typography>
                      <Box sx={{ minWidth: 0 }}>
                        <CompanyCell device={d} />
                      </Box>
                    </Stack>
                  )}
                  <Box
                    sx={{
                      pt: 0.5,
                      mt: 0.25,
                      borderTop: "1px solid",
                      borderColor: "divider",
                      display: "flex",
                    }}
                  >
                    <ActionRow
                      d={d}
                      isStatusOpen={isStatusOpen}
                      isConfigOpen={isConfigOpen}
                      sendingUpdateImei={sendingUpdateImei}
                      onToggleStatus={onToggleStatus}
                      onToggleConfig={onToggleConfig}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      onUnlink={onUnlink}
                      onSwap={onSwap}
                      onLogs={onLogs}
                      onUpdate={onUpdate}
                      navigate={navigate}
                      justifyContent="space-between"
                      compact
                    />
                  </Box>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      </Box>
    );
  }

  return (
    // Flex-based sizing instead of calc(100vh - 300px). The container fills
    // whatever vertical space its parent gives it, then the table body
    // scrolls inside; the sticky header stays put. Parent must be a flex
    // column with a bounded height for this to do something useful (both
    // DevicesPage and CompanyDevicesTab already are).
    <TableContainer ref={scrollRef} sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 40 }}>#</TableCell>
            {/* IMEI is a fixed 15-digit string at monospace 0.7rem; pin
                the column so it never wraps or gets cropped. */}
            <TableCell sx={{ width: 140, whiteSpace: "nowrap" }}>
              IMEI
            </TableCell>
            <TableCell>Name</TableCell>
            <TableCell sx={{ width: 70 }}>Type</TableCell>
            <TableCell>Model</TableCell>
            {showCompany && (
              // Slightly narrower so the IMEI column gets the extra
              // breathing room — company name + manufacturing_id can
              // wrap, IMEI cannot.
              <TableCell sx={{ width: 150 }}>Company</TableCell>
            )}
            <TableCell sx={{ width: 220, whiteSpace: "nowrap" }} align="right">
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading && items.length === 0 && (
            <TableRow>
              <TableCell colSpan={showCompany ? 7 : 6} align="center" sx={{ py: 4 }}>
                <CircularProgress size={20} />
              </TableCell>
            </TableRow>
          )}
          {!loading && items.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={showCompany ? 7 : 6}
                align="center"
                sx={{ py: 4, color: "text.secondary" }}
              >
                No devices yet.{" "}
                {onAddFirst && (
                  <MuiLink component="button" onClick={onAddFirst}>
                    Add the first one
                  </MuiLink>
                )}
              </TableCell>
            </TableRow>
          )}
          {items.map((d, i) => {
            const isStatusOpen = activeStatusImei === d.IMEI;
            const isConfigOpen = activeConfigImei === d.IMEI;
            return (
              <TableRow key={d._id} hover selected={isStatusOpen || isConfigOpen}>
                <TableCell sx={{ color: "text.secondary" }}>{i + 1}</TableCell>
                <TableCell
                  sx={{
                    width: 140,
                    fontFamily: "monospace",
                    fontSize: "0.7rem",
                    whiteSpace: "nowrap",
                  }}
                >
                  {d.IMEI}
                </TableCell>
                <TableCell sx={{ fontWeight: 500 }}>
                  {d.device_name || "—"}
                </TableCell>
                <TableCell>
                  <TypeChip type={Number(d.device_type)} />
                </TableCell>
                <TableCell sx={{ color: "text.secondary", fontSize: "0.75rem" }}>
                  {d.model || "—"}
                </TableCell>
                {showCompany && (
                  <TableCell>
                    <CompanyCell device={d} />
                  </TableCell>
                )}
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  <ActionRow
                    d={d}
                    isStatusOpen={isStatusOpen}
                    isConfigOpen={isConfigOpen}
                    sendingUpdateImei={sendingUpdateImei}
                    onToggleStatus={onToggleStatus}
                    onToggleConfig={onToggleConfig}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onUnlink={onUnlink}
                    onSwap={onSwap}
                    onLogs={onLogs}
                    onUpdate={onUpdate}
                    navigate={navigate}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
